import React, { useCallback, useEffect, useMemo, useState } from "react";
import { get, ref, set } from "firebase/database";
import { db } from "../../lib/firebase";
import { CONFIG } from "../../config";
import "./PdfConfig.css";
import { normalizePdfPageSlug, toPdfPagePath } from "../../utils/pdfRouting";
import {
  DEFAULT_PDF_STYLE_CONFIG,
  createPdfDocumentDraft,
  normalizePdfConfig,
  normalizePdfStyleConfig,
  PDF_PLACEMENT_TYPES,
  PDF_SECTION_PAGE_OPTIONS,
} from "../../utils/pdfDocuments";

const UPLOAD_URL = CONFIG.UPLOAD_URL;
const FILES_BASE = CONFIG.IMAGES_BASE;

const DEFAULT_FORM = {
  documents: [],
  styleConfig: DEFAULT_PDF_STYLE_CONFIG,
};

const isNonEmptyString = (value) =>
  typeof value === "string" && value.trim() !== "";

const clampNumber = (value, min, max, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

const HEX_COLOR_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const toHexColor = (value, fallback) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!HEX_COLOR_RE.test(normalized)) return fallback;
  if (normalized.length === 4) {
    return `#${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}${normalized[3]}${normalized[3]}`;
  }
  return normalized;
};

const toFileUrl = (value) => {
  if (!isNonEmptyString(value)) return "";
  const normalized = value.trim();
  if (/^https?:\/\//i.test(normalized) || normalized.startsWith("//")) {
    return normalized;
  }
  if (normalized.startsWith("/")) return normalized;
  return `${FILES_BASE}/${encodeURIComponent(normalized)}`;
};

const isPdfFile = (file) => {
  if (!file) return false;
  const lowerName = String(file.name || "").toLowerCase();
  return file.type === "application/pdf" || lowerName.endsWith(".pdf");
};

const normalizeSectionPage = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, "");
  return normalized || "home";
};

export default function PdfConfig() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState("");
  const [deletingFileId, setDeletingFileId] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const snap = await get(ref(db, "pdfconfig"));
        const normalized = normalizePdfConfig(snap.val());
        setForm({
          documents: normalized.documents.length
            ? normalized.documents
            : [createPdfDocumentDraft(0)],
          styleConfig: normalized.styleConfig || DEFAULT_PDF_STYLE_CONFIG,
        });
      } catch (e) {
        console.error(e);
        setError("Failed to load PDF configuration.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const documents = useMemo(
    () => (Array.isArray(form.documents) ? form.documents : []),
    [form.documents]
  );
  const styleConfig = useMemo(
    () => normalizePdfStyleConfig(form.styleConfig),
    [form.styleConfig]
  );

  const updateDocumentField = (docId, field, value) => {
    setForm((prev) => ({
      ...prev,
      documents: (prev.documents || []).map((doc) =>
        doc.id === docId ? { ...doc, [field]: value } : doc
      ),
    }));
  };

  const updateStyleField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      styleConfig: {
        ...normalizePdfStyleConfig(prev.styleConfig),
        [field]: value,
      },
    }));
  };

  const addDocument = () => {
    setForm((prev) => ({
      ...prev,
      documents: [...(prev.documents || []), createPdfDocumentDraft(prev.documents.length)],
    }));
  };

  const removeDocument = (docId) => {
    setForm((prev) => {
      const next = (prev.documents || []).filter((doc) => doc.id !== docId);
      return {
        ...prev,
        documents: next.length ? next : [createPdfDocumentDraft(0)],
      };
    });
  };

  const uploadPdf = useCallback(async (docId, file) => {
    if (!file) return;
    if (!isPdfFile(file)) {
      setError("Please choose a .pdf file.");
      return;
    }

    try {
      setUploadingId(docId);
      setError("");
      setMessage("");
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(UPLOAD_URL, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`Upload failed (${response.status}) ${text}`);
      }

      const data = await response.json();
      if (!isNonEmptyString(data?.filename)) {
        throw new Error("Upload succeeded but filename is missing.");
      }

      setForm((prev) => ({
        ...prev,
        documents: (prev.documents || []).map((doc) =>
          doc.id === docId
            ? {
                ...doc,
                pdfFileName: data.filename.trim(),
                externalUrl: "",
              }
            : doc
        ),
      }));
      setMessage("PDF uploaded. Save to publish changes.");
    } catch (e) {
      console.error(e);
      setError(e?.message || "PDF upload failed.");
    } finally {
      setUploadingId("");
    }
  }, []);

  const deleteUploadedPdf = useCallback(
    async (docId) => {
      const target = documents.find((doc) => doc.id === docId);
      const filename = target?.pdfFileName;
      if (!isNonEmptyString(filename)) return;

      const shouldDelete = window.confirm(
        `Delete uploaded PDF "${filename}" from server storage?`
      );
      if (!shouldDelete) return;

      try {
        setDeletingFileId(docId);
        setError("");
        setMessage("");

        const response = await fetch(
          `${FILES_BASE}/${encodeURIComponent(filename.trim())}`,
          { method: "DELETE" }
        );
        if (!response.ok) {
          const text = await response.text().catch(() => "");
          throw new Error(`Delete failed (${response.status}) ${text}`);
        }

        setForm((prev) => ({
          ...prev,
          documents: (prev.documents || []).map((doc) =>
            doc.id === docId ? { ...doc, pdfFileName: "" } : doc
          ),
        }));
        setMessage("Uploaded PDF deleted. Save to publish changes.");
      } catch (e) {
        console.error(e);
        setError(e?.message || "Failed to delete uploaded PDF.");
      } finally {
        setDeletingFileId("");
      }
    },
    [documents]
  );

  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const payloadStyleConfig = normalizePdfStyleConfig(styleConfig);
      const payloadDocuments = documents.map((doc, index) => ({
        id: String(doc.id || `pdf-${index + 1}`),
        enabled: Boolean(doc.enabled),
        title: isNonEmptyString(doc.title)
          ? doc.title.trim()
          : `PDF Document ${index + 1}`,
        description: isNonEmptyString(doc.description) ? doc.description.trim() : "",
        buttonText: isNonEmptyString(doc.buttonText)
          ? doc.buttonText.trim()
          : "Download PDF",
        pageTitle: isNonEmptyString(doc.pageTitle)
          ? doc.pageTitle.trim()
          : "Documents",
        pageDescription: isNonEmptyString(doc.pageDescription)
          ? doc.pageDescription.trim()
          : "",
        metaTitle: isNonEmptyString(doc.metaTitle) ? doc.metaTitle.trim() : "",
        metaDescription: isNonEmptyString(doc.metaDescription)
          ? doc.metaDescription.trim()
          : "",
        metaKeywords: isNonEmptyString(doc.metaKeywords)
          ? doc.metaKeywords.trim()
          : "",
        pageSlug: normalizePdfPageSlug(doc.pageSlug),
        pdfFileName: isNonEmptyString(doc.pdfFileName) ? doc.pdfFileName.trim() : "",
        externalUrl: isNonEmptyString(doc.externalUrl) ? doc.externalUrl.trim() : "",
        placementType:
          doc.placementType === PDF_PLACEMENT_TYPES.SECTION
            ? PDF_PLACEMENT_TYPES.SECTION
            : PDF_PLACEMENT_TYPES.PAGE,
        sectionPage: normalizeSectionPage(doc.sectionPage),
        sectionOrder: clampNumber(doc.sectionOrder, 0, 9999, index + 1),
        viewerHeight: clampNumber(doc.viewerHeight, 320, 1600, 700),
        cardTitleColor: isNonEmptyString(doc.cardTitleColor)
          ? doc.cardTitleColor.trim()
          : DEFAULT_PDF_STYLE_CONFIG.cardTitleColor,
        cardTitleSize: clampNumber(
          doc.cardTitleSize,
          12,
          42,
          DEFAULT_PDF_STYLE_CONFIG.cardTitleSize
        ),
        cardDescriptionColor: isNonEmptyString(doc.cardDescriptionColor)
          ? doc.cardDescriptionColor.trim()
          : DEFAULT_PDF_STYLE_CONFIG.cardDescriptionColor,
        cardDescriptionSize: clampNumber(
          doc.cardDescriptionSize,
          10,
          30,
          DEFAULT_PDF_STYLE_CONFIG.cardDescriptionSize
        ),
        updatedAt: Date.now(),
      }));

      await set(ref(db, "pdfconfig"), {
        documents: payloadDocuments,
        styleConfig: payloadStyleConfig,
        updatedAt: Date.now(),
      });

      setMessage("PDF configuration saved.");
    } catch (e) {
      console.error(e);
      setError(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const slugPreviewMap = useMemo(() => {
    const out = {};
    documents.forEach((doc) => {
      out[doc.id] = toPdfPagePath(doc.pageSlug);
    });
    return out;
  }, [documents]);

  if (loading) return <div className="pdfcfg-loading">Loading…</div>;

  return (
    <section className="pdfcfg-wrap">
      <div className="pdfcfg-header">
        <h2 className="pdfcfg-title">PDF section configuration</h2>
        <button className="pdfcfg-btn" type="button" onClick={addDocument}>
          + Add PDF
        </button>
      </div>

      <p className="pdfcfg-note">
        Add multiple PDFs. Choose where each PDF appears: dedicated page (with custom
        slug) or page section (home/contact/about/reviews with order).
      </p>

      <article className="pdfcfg-style-card">
        <div className="pdfcfg-style-head">
          <h3 className="pdfcfg-subtitle">Card typography and color</h3>
          <label className="pdfcfg-check compact">
            <input
              type="checkbox"
              checked={Boolean(styleConfig.applyToAllCards)}
              onChange={(e) =>
                updateStyleField("applyToAllCards", e.target.checked)
              }
            />
            <span>Apply this style to all cards</span>
          </label>
        </div>

        <div className="pdfcfg-row">
          <div className="pdfcfg-col">
            <label className="pdfcfg-label">Section title size (px)</label>
            <input
              type="number"
              min={18}
              max={64}
              value={styleConfig.sectionTitleSize}
              onChange={(e) =>
                updateStyleField("sectionTitleSize", e.target.value)
              }
            />
          </div>
          <div className="pdfcfg-col">
            <label className="pdfcfg-label">Section title color</label>
            <input
              type="color"
              value={toHexColor(
                styleConfig.sectionTitleColor,
                DEFAULT_PDF_STYLE_CONFIG.sectionTitleColor
              )}
              onChange={(e) =>
                updateStyleField("sectionTitleColor", e.target.value)
              }
            />
          </div>
        </div>

        <div className="pdfcfg-row">
          <div className="pdfcfg-col">
            <label className="pdfcfg-label">Section subtitle size (px)</label>
            <input
              type="number"
              min={10}
              max={30}
              value={styleConfig.sectionSubtitleSize}
              onChange={(e) =>
                updateStyleField("sectionSubtitleSize", e.target.value)
              }
            />
          </div>
          <div className="pdfcfg-col">
            <label className="pdfcfg-label">Section subtitle color</label>
            <input
              type="color"
              value={toHexColor(
                styleConfig.sectionSubtitleColor,
                DEFAULT_PDF_STYLE_CONFIG.sectionSubtitleColor
              )}
              onChange={(e) =>
                updateStyleField("sectionSubtitleColor", e.target.value)
              }
            />
          </div>
        </div>

        <div className="pdfcfg-row">
          <div className="pdfcfg-col">
            <label className="pdfcfg-label">Card title size (px)</label>
            <input
              type="number"
              min={12}
              max={42}
              value={styleConfig.cardTitleSize}
              onChange={(e) => updateStyleField("cardTitleSize", e.target.value)}
            />
          </div>
          <div className="pdfcfg-col">
            <label className="pdfcfg-label">Card title color</label>
            <input
              type="color"
              value={toHexColor(
                styleConfig.cardTitleColor,
                DEFAULT_PDF_STYLE_CONFIG.cardTitleColor
              )}
              onChange={(e) => updateStyleField("cardTitleColor", e.target.value)}
            />
          </div>
        </div>

        <div className="pdfcfg-row">
          <div className="pdfcfg-col">
            <label className="pdfcfg-label">Card description size (px)</label>
            <input
              type="number"
              min={10}
              max={30}
              value={styleConfig.cardDescriptionSize}
              onChange={(e) =>
                updateStyleField("cardDescriptionSize", e.target.value)
              }
            />
          </div>
          <div className="pdfcfg-col">
            <label className="pdfcfg-label">Card description color</label>
            <input
              type="color"
              value={toHexColor(
                styleConfig.cardDescriptionColor,
                DEFAULT_PDF_STYLE_CONFIG.cardDescriptionColor
              )}
              onChange={(e) =>
                updateStyleField("cardDescriptionColor", e.target.value)
              }
            />
          </div>
        </div>

        <div className="pdfcfg-row">
          <div className="pdfcfg-col">
            <label className="pdfcfg-label">Viewer title size (px)</label>
            <input
              type="number"
              min={14}
              max={48}
              value={styleConfig.viewerTitleSize}
              onChange={(e) => updateStyleField("viewerTitleSize", e.target.value)}
            />
          </div>
          <div className="pdfcfg-col">
            <label className="pdfcfg-label">Viewer title color</label>
            <input
              type="color"
              value={toHexColor(
                styleConfig.viewerTitleColor,
                DEFAULT_PDF_STYLE_CONFIG.viewerTitleColor
              )}
              onChange={(e) => updateStyleField("viewerTitleColor", e.target.value)}
            />
          </div>
        </div>

        <div className="pdfcfg-row">
          <div className="pdfcfg-col">
            <label className="pdfcfg-label">Viewer description size (px)</label>
            <input
              type="number"
              min={10}
              max={30}
              value={styleConfig.viewerDescriptionSize}
              onChange={(e) =>
                updateStyleField("viewerDescriptionSize", e.target.value)
              }
            />
          </div>
          <div className="pdfcfg-col">
            <label className="pdfcfg-label">Viewer description color</label>
            <input
              type="color"
              value={toHexColor(
                styleConfig.viewerDescriptionColor,
                DEFAULT_PDF_STYLE_CONFIG.viewerDescriptionColor
              )}
              onChange={(e) =>
                updateStyleField("viewerDescriptionColor", e.target.value)
              }
            />
          </div>
        </div>

        {!styleConfig.applyToAllCards && (
          <p className="pdfcfg-note">
            Global styles are still used as fallback. Per-card style fields below can
            override each card.
          </p>
        )}
      </article>

      {documents.map((doc, index) => {
        const activePdfUrl = isNonEmptyString(doc.externalUrl)
          ? doc.externalUrl.trim()
          : toFileUrl(doc.pdfFileName);
        const pathPreview = slugPreviewMap[doc.id] || toPdfPagePath(doc.pageSlug);
        const isSectionPlacement = doc.placementType === PDF_PLACEMENT_TYPES.SECTION;

        return (
          <article key={doc.id} className="pdfcfg-doc-card">
            <div className="pdfcfg-doc-head">
              <h3 className="pdfcfg-subtitle">PDF #{index + 1}</h3>
              <div className="pdfcfg-doc-head-actions">
                <label className="pdfcfg-check compact">
                  <input
                    type="checkbox"
                    checked={Boolean(doc.enabled)}
                    onChange={(e) =>
                      updateDocumentField(doc.id, "enabled", e.target.checked)
                    }
                  />
                  <span>Enabled</span>
                </label>
                <button
                  className="pdfcfg-btn danger"
                  type="button"
                  onClick={() => removeDocument(doc.id)}
                >
                  Remove
                </button>
              </div>
            </div>

            <div className="pdfcfg-row">
              <div className="pdfcfg-col">
                <label className="pdfcfg-label">Card title</label>
                <input
                  type="text"
                  value={doc.title || ""}
                  onChange={(e) =>
                    updateDocumentField(doc.id, "title", e.target.value)
                  }
                  placeholder="Project Portfolio"
                />
              </div>

              <div className="pdfcfg-col">
                <label className="pdfcfg-label">Download button label</label>
                <input
                  type="text"
                  value={doc.buttonText || ""}
                  onChange={(e) =>
                    updateDocumentField(doc.id, "buttonText", e.target.value)
                  }
                  placeholder="Download PDF"
                />
              </div>
            </div>

            <label className="pdfcfg-label">Description</label>
            <textarea
              rows={3}
              value={doc.description || ""}
              onChange={(e) =>
                updateDocumentField(doc.id, "description", e.target.value)
              }
              placeholder="Short summary shown on the PDF card."
            />

            <div className="pdfcfg-row">
              <div className="pdfcfg-col">
                <label className="pdfcfg-label">Card title size (px)</label>
                <input
                  type="number"
                  min={12}
                  max={42}
                  value={doc.cardTitleSize ?? DEFAULT_PDF_STYLE_CONFIG.cardTitleSize}
                  onChange={(e) =>
                    updateDocumentField(doc.id, "cardTitleSize", e.target.value)
                  }
                  disabled={Boolean(styleConfig.applyToAllCards)}
                />
              </div>

              <div className="pdfcfg-col">
                <label className="pdfcfg-label">Card title color</label>
                <input
                  type="color"
                  value={toHexColor(
                    doc.cardTitleColor,
                    DEFAULT_PDF_STYLE_CONFIG.cardTitleColor
                  )}
                  onChange={(e) =>
                    updateDocumentField(doc.id, "cardTitleColor", e.target.value)
                  }
                  disabled={Boolean(styleConfig.applyToAllCards)}
                />
              </div>
            </div>

            <div className="pdfcfg-row">
              <div className="pdfcfg-col">
                <label className="pdfcfg-label">Card description size (px)</label>
                <input
                  type="number"
                  min={10}
                  max={30}
                  value={
                    doc.cardDescriptionSize ??
                    DEFAULT_PDF_STYLE_CONFIG.cardDescriptionSize
                  }
                  onChange={(e) =>
                    updateDocumentField(
                      doc.id,
                      "cardDescriptionSize",
                      e.target.value
                    )
                  }
                  disabled={Boolean(styleConfig.applyToAllCards)}
                />
              </div>

              <div className="pdfcfg-col">
                <label className="pdfcfg-label">Card description color</label>
                <input
                  type="color"
                  value={toHexColor(
                    doc.cardDescriptionColor,
                    DEFAULT_PDF_STYLE_CONFIG.cardDescriptionColor
                  )}
                  onChange={(e) =>
                    updateDocumentField(
                      doc.id,
                      "cardDescriptionColor",
                      e.target.value
                    )
                  }
                  disabled={Boolean(styleConfig.applyToAllCards)}
                />
              </div>
            </div>

            {styleConfig.applyToAllCards && (
              <p className="pdfcfg-note">
                Per-card title/description style is disabled because global apply is
                enabled.
              </p>
            )}

            <div className="pdfcfg-row">
              <div className="pdfcfg-col">
                <label className="pdfcfg-label">Upload PDF file</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => uploadPdf(doc.id, e.target.files?.[0])}
                  disabled={uploadingId === doc.id}
                />
                {uploadingId === doc.id && (
                  <p className="pdfcfg-note">Uploading PDF…</p>
                )}
              </div>

              <div className="pdfcfg-col">
                <label className="pdfcfg-label">Uploaded filename</label>
                <input
                  type="text"
                  value={doc.pdfFileName || ""}
                  onChange={(e) =>
                    updateDocumentField(doc.id, "pdfFileName", e.target.value)
                  }
                  placeholder="portfolio.pdf"
                />
              </div>
            </div>

            <div className="pdfcfg-row">
              <div className="pdfcfg-col">
                <label className="pdfcfg-label">External PDF URL (optional)</label>
                <input
                  type="text"
                  value={doc.externalUrl || ""}
                  onChange={(e) =>
                    updateDocumentField(doc.id, "externalUrl", e.target.value)
                  }
                  placeholder="https://example.com/file.pdf"
                />
                <p className="pdfcfg-note">
                  If external URL is set, it overrides uploaded filename.
                </p>
              </div>

              <div className="pdfcfg-col">
                <label className="pdfcfg-label">Placement type</label>
                <select
                  value={doc.placementType || PDF_PLACEMENT_TYPES.PAGE}
                  onChange={(e) =>
                    updateDocumentField(doc.id, "placementType", e.target.value)
                  }
                >
                  <option value={PDF_PLACEMENT_TYPES.PAGE}>Dedicated page</option>
                  <option value={PDF_PLACEMENT_TYPES.SECTION}>Page section</option>
                </select>
              </div>
            </div>

            {isSectionPlacement ? (
              <div className="pdfcfg-row">
                <div className="pdfcfg-col">
                  <label className="pdfcfg-label">Section page</label>
                  <input
                    type="text"
                    list={`pdfcfg-section-pages-${doc.id}`}
                    value={doc.sectionPage || "home"}
                    onChange={(e) =>
                      updateDocumentField(doc.id, "sectionPage", e.target.value)
                    }
                    placeholder="home"
                  />
                  <datalist id={`pdfcfg-section-pages-${doc.id}`}>
                    {PDF_SECTION_PAGE_OPTIONS.map((page) => (
                      <option key={page} value={page} />
                    ))}
                  </datalist>
                  <p className="pdfcfg-note">
                    Enter the page key where this section should appear (example: home,
                    about, contact, reviews).
                  </p>
                </div>

                <div className="pdfcfg-col">
                  <label className="pdfcfg-label">Section order number</label>
                  <input
                    type="number"
                    min={0}
                    max={9999}
                    value={doc.sectionOrder ?? index + 1}
                    onChange={(e) =>
                      updateDocumentField(doc.id, "sectionOrder", e.target.value)
                    }
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="pdfcfg-row">
                  <div className="pdfcfg-col">
                    <label className="pdfcfg-label">Dedicated page title</label>
                    <input
                      type="text"
                      value={doc.pageTitle || ""}
                      onChange={(e) =>
                        updateDocumentField(doc.id, "pageTitle", e.target.value)
                      }
                      placeholder="Documents"
                    />
                  </div>

                  <div className="pdfcfg-col">
                    <label className="pdfcfg-label">Dedicated page slug</label>
                    <input
                      type="text"
                      value={doc.pageSlug || ""}
                      onChange={(e) =>
                        updateDocumentField(doc.id, "pageSlug", e.target.value)
                      }
                      placeholder="portfolio"
                    />
                    <p className="pdfcfg-note">
                      Route preview: <code>{pathPreview}</code>
                    </p>
                  </div>
                </div>

                <label className="pdfcfg-label">Dedicated page description</label>
                <textarea
                  rows={2}
                  value={doc.pageDescription || ""}
                  onChange={(e) =>
                    updateDocumentField(doc.id, "pageDescription", e.target.value)
                  }
                  placeholder="Short intro text for the dedicated PDF page."
                />

                <div className="pdfcfg-row">
                  <div className="pdfcfg-col">
                    <label className="pdfcfg-label">Meta title</label>
                    <input
                      type="text"
                      value={doc.metaTitle || ""}
                      onChange={(e) =>
                        updateDocumentField(doc.id, "metaTitle", e.target.value)
                      }
                      placeholder="Portfolio | Regency Interior"
                    />
                  </div>

                  <div className="pdfcfg-col">
                    <label className="pdfcfg-label">Meta keywords</label>
                    <input
                      type="text"
                      value={doc.metaKeywords || ""}
                      onChange={(e) =>
                        updateDocumentField(doc.id, "metaKeywords", e.target.value)
                      }
                      placeholder="portfolio, interior design, regency"
                    />
                  </div>
                </div>

                <label className="pdfcfg-label">Meta description</label>
                <textarea
                  rows={2}
                  value={doc.metaDescription || ""}
                  onChange={(e) =>
                    updateDocumentField(doc.id, "metaDescription", e.target.value)
                  }
                  placeholder="SEO description for this dedicated PDF page."
                />
              </>
            )}

            <div className="pdfcfg-row">
              <div className="pdfcfg-col">
                <label className="pdfcfg-label">Viewer height (px)</label>
                <input
                  type="number"
                  min={320}
                  max={1600}
                  value={doc.viewerHeight ?? 700}
                  onChange={(e) =>
                    updateDocumentField(doc.id, "viewerHeight", e.target.value)
                  }
                />
              </div>

              <div className="pdfcfg-col">
                <label className="pdfcfg-label">Current PDF</label>
                {activePdfUrl ? (
                  <div className="pdfcfg-file-actions">
                    <a
                      className="pdfcfg-link"
                      href={activePdfUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open current document
                    </a>
                    {isNonEmptyString(doc.pdfFileName) && (
                      <button
                        className="pdfcfg-btn danger inline"
                        type="button"
                        onClick={() => deleteUploadedPdf(doc.id)}
                        disabled={deletingFileId === doc.id}
                      >
                        {deletingFileId === doc.id
                          ? "Deleting..."
                          : "Delete uploaded PDF"}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="pdfcfg-file-actions">
                    <p className="pdfcfg-note">No PDF source set yet.</p>
                    {isNonEmptyString(doc.pdfFileName) && (
                      <button
                        className="pdfcfg-btn danger inline"
                        type="button"
                        onClick={() => deleteUploadedPdf(doc.id)}
                        disabled={deletingFileId === doc.id}
                      >
                        {deletingFileId === doc.id
                          ? "Deleting..."
                          : "Delete uploaded PDF"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </article>
        );
      })}

      {error && <p className="pdfcfg-error">{error}</p>}
      {message && <p className="pdfcfg-success">{message}</p>}

      <div className="pdfcfg-actions">
        <button className="pdfcfg-btn" type="button" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save PDF config"}
        </button>
      </div>
    </section>
  );
}
